import { useMutation, useQuery } from '@apollo/client';
import { ObservablePrimitive } from '@legendapp/state';
import { useObservable } from '@legendapp/state/react';
import * as icon from '@mui/icons-material';
import {
  Card,
  CircularProgress,
  Container,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { FormSubmitRow } from 'components/common/form/submit-row';
import { MetadataItem } from 'components/common/metadata';
import { MetadataItemCopy } from 'components/common/metadata/metadata-item';
import { AudioPreviewButton } from 'components/views/audio/player';
import { IvrFilesQuery, IvrFoldersQuery } from 'components/views/campaign/vendors/ivrs.graphql';
import { TcrCard } from 'components/views/campaign/vendors/tcr';
import { FormAutocomplete, FormTextField } from 'components/views/forms/form-text-field';
import { RadioForm } from 'components/views/forms/radio-form';
import { useDemoMode } from 'providers/demo-mode';
import { FC, FormEvent, useEffect, useState } from 'react';
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router';
import {
  CampaignVendors,
  CampaignVendors_campaign,
  CampaignVendorsVariables,
} from 'types/apollo/CampaignVendors';
import {
  IvrFiles,
  IvrFiles_getIvrFiles,
  IvrFiles_getIvrFiles_files,
  IvrFilesVariables,
} from 'types/apollo/IvrFiles';
import { IvrFolders } from 'types/apollo/IvrFolders';
import { ProvisionTelnyx, ProvisionTelnyxVariables } from 'types/apollo/ProvisionTelnyx';
import {
  TelnyxSearch,
  TelnyxSearch_telnyxSearchPhones,
  TelnyxSearchVariables,
} from 'types/apollo/TelnyxSearch';
import { formatDate } from 'utils/date';
import { A2pCards } from './a2p';
import { CampaignIvrUrlsCard } from './campaign-ivr-urls-card';
import { statesAndCities } from './cities';
import { ProvisionTelnyxMutation, TelnyxQuery } from './telnyx.graphql';
import { VendorSummary } from './vendor-summary';
import { CampaignVendorsQuery } from './vendors.graphql';

interface VendorFormData {
  searchCity: string;
  cityOptions: string[];
  searchState: string;
  searchStartsWith: string;
  phone: string;
  ivrFolder: string | null;
  ivrFolderOptions: string[];
  volume: number;
}

interface ProvisionVendorFormData {
  phone: string;
  vendor: '' | 'Telnyx' | 'Twilio';
}

const states = Object.keys(statesAndCities).sort();

interface VendorRadioParams {
  phones: TelnyxSearch_telnyxSearchPhones[] | null;
  search: () => Promise<any>;
  searching: boolean;
}

const VendorRadio = (props: VendorRadioParams) => {
  const form = useFormContext<VendorFormData>();
  const [searching, setSearching] = useState<boolean>(false);

  const handleClick = async () => {
    setSearching(true);
    try {
      await props.search();
    } finally {
      setSearching(false);
    }
  };
  const searchState = form.watch('searchState');

  useEffect(() => {
    const cities = statesAndCities[searchState];
    form.setValue('searchCity', cities[0]);
    form.setValue('cityOptions', [...cities].sort());
  }, [searchState]);

  return (
    <>
      <Stack direction="row" spacing={2} display="flex" alignItems="center">
        <FormTextField
          form={form}
          name="searchStartsWith"
          label="Starts with / area code"
          optional
          sx={{ width: 200 }}
        />
        <FormAutocomplete
          form={form}
          label="City"
          name="searchCity"
          optionsName="cityOptions"
          optional
          sx={{ width: 400 }}
        />
        <FormTextField form={form} label="State" name="searchState" optional sx={{ width: 100 }}>
          {states.map((state) => (
            <MenuItem key={state} value={state}>
              {state}
            </MenuItem>
          ))}
        </FormTextField>
        <IconButton size="small" onClick={handleClick} disabled={props.searching || searching}>
          <icon.SearchOutlined />
          {(props.searching || searching) && <CircularProgress size={18} sx={{ marginRight: 1 }} />}
        </IconButton>
      </Stack>
      <Controller
        control={form.control}
        name="phone"
        rules={{
          required: true,
        }}
        render={({ field }) => (
          <RadioGroup {...field}>
            {props.phones?.map(({ monthlyCost, oneTimeCost, phoneNumber, location }) => (
              <FormControlLabel
                key={phoneNumber}
                value={phoneNumber}
                control={<Radio required />}
                label={
                  <Stack direction="row">
                    <Typography fontSize={16}>{phoneNumber}</Typography>
                    <Typography fontSize={12} marginLeft={5}>
                      (${Number(oneTimeCost)} + ${Number(monthlyCost)}
                      /month)
                    </Typography>
                    {location && (
                      <Typography fontSize={12} marginLeft={5}>
                        {location}
                      </Typography>
                    )}
                  </Stack>
                }
              />
            ))}
          </RadioGroup>
        )}
      />
      {props.phones && props.phones.length == 0 && (
        <Typography fontWeight="bold">No phones available for this city/state</Typography>
      )}
    </>
  );
};

export const IvrRow = ({
  file,
  currentlyPlayingUrl,
}: {
  file: IvrFiles_getIvrFiles_files;
  currentlyPlayingUrl: ObservablePrimitive<string>;
}) => (
  <Stack direction="row" alignItems="center">
    <AudioPreviewButton url={file.url} currentlyPlayingUrl={currentlyPlayingUrl} showSeekBar />
    <ListItemText
      sx={{ marginLeft: 1 }}
      primary={file.name}
      secondary={`Modified: ${formatDate(file.updatedAt)}`}
    />
  </Stack>
);

const TelnyxRadio: FC = () => {
  const form = useFormContext<VendorFormData>();
  const [phones, setPhones] = useState<TelnyxSearch_telnyxSearchPhones[] | null>(null);
  const [files, setFiles] = useState<IvrFiles_getIvrFiles | null>(null);
  const currentlyPlayingUrl = useObservable<string>();

  const { loading: searching, refetch } = useQuery<TelnyxSearch, TelnyxSearchVariables>(
    TelnyxQuery,
    {
      skip: true,
      variables: {
        city: form.getValues('searchCity'),
        region: form.getValues('searchState'),
        startsWith: form.getValues('searchStartsWith'),
      },
      onCompleted: (d) => {
        setPhones(d.telnyxSearchPhones);
      },
    },
  );

  const { refetch: searchFiles } = useQuery<IvrFiles, IvrFilesVariables>(IvrFilesQuery, {
    skip: true,
    onCompleted: (d) => {
      if (d?.getIvrFiles) {
        setFiles(d.getIvrFiles);
      }
    },
  });

  useQuery<IvrFolders>(IvrFoldersQuery, {
    onCompleted: (d) => {
      if (d?.getIvrFolders) {
        form.setValue('ivrFolderOptions', d.getIvrFolders);
      }
    },
  });

  const ivrFolder = form.watch('ivrFolder');

  useEffect(() => {
    if (ivrFolder && form.getValues('ivrFolderOptions')?.includes(ivrFolder)) {
      searchFiles({
        path: ivrFolder,
      });
      currentlyPlayingUrl.set('none');
    }
  }, [ivrFolder]);

  const handleSearch = () => {
    return refetch({
      city: form.getValues('searchCity'),
      region: form.getValues('searchState'),
      startsWith: form.getValues('searchStartsWith'),
    });
  };

  return (
    <Stack direction="column">
      <VendorRadio phones={phones} search={handleSearch} searching={searching} />
      <Stack direction="row">
        <FormAutocomplete
          form={form}
          name="ivrFolder"
          optionsName="ivrFolderOptions"
          label="IVR folder name in AWS"
          freeSolo={false}
          sx={{ width: 500 }}
        />
        <FormTextField
          form={form}
          name="volume"
          sx={{ width: 100, marginLeft: 2 }}
          type="number"
          inputProps={{
            min: 0,
            max: 100,
            inputMode: 'numeric',
            pattern: '[0-9]*',
          }}
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          }}
        />
      </Stack>

      {files && (
        <div>
          <Typography>
            <Typography component="span">Files in </Typography>
            <Typography component="span" fontWeight="bold">
              {files.name}
            </Typography>
          </Typography>
          {files.files.map((f) => (
            <IvrRow key={f.url} file={f} currentlyPlayingUrl={currentlyPlayingUrl} />
          ))}
        </div>
      )}
    </Stack>
  );
};

export const Vendors: FC = () => {
  const { isInDemoMode } = useDemoMode();
  const { id, campaignId } = useParams() as { id?: string; campaignId: string };
  const [state, setState] = useState<Omit<CampaignVendors_campaign, '__typename'>>({
    id: '',
    vendorAccounts: [],
    twilioSubAccount: null,
    transferPhoneOutbound: null,
    transferPhoneInbound: null,
    ivrAudioUrls: null,
  });

  const telnyxForm = useForm<VendorFormData>({
    defaultValues: {
      cityOptions: [],
      searchCity: 'Salt Lake City',
      searchState: 'UT',
      searchStartsWith: '',
      phone: '',
      ivrFolder: null,
      ivrFolderOptions: [],
      volume: 100,
    },
    mode: 'all',
  });

  const form = useForm<ProvisionVendorFormData>({
    defaultValues: {
      vendor: 'Telnyx',
    },
  });

  const { loading, refetch } = useQuery<CampaignVendors, CampaignVendorsVariables>(
    CampaignVendorsQuery,
    {
      variables: { campaignId },
      onCompleted: (data) => {
        if (data.campaign) {
          setState(data.campaign);
        }
      },
    },
  );

  const [provisionTelnyx, { loading: provisioning }] = useMutation<
    ProvisionTelnyx,
    ProvisionTelnyxVariables
  >(ProvisionTelnyxMutation);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const ivrFolder = telnyxForm.getValues('ivrFolder');
    if (!ivrFolder) {
      return;
    }

    await provisionTelnyx({
      variables: {
        provisionTelnyx: {
          campaignId: state.id,
          ivrFolder,
          agentPhoneNumber: telnyxForm.getValues('phone'),
          volume: Number(telnyxForm.getValues('volume')),
        },
      },
    });

    await refetch();
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        p: {
          xs: 2,
          md: 3,
        },
      }}
    >
      {!isInDemoMode && (
        <Card variant="outlined" sx={{ padding: 2 }}>
          {loading && <LinearProgress sx={{ height: '2px', mb: 1 }} />}
          <Typography variant="h4">Twilio</Typography>
          <MetadataItem
            label="Twilio Subaccount"
            value={state?.twilioSubAccount?.friendlyName ?? 'Not setup yet'}
          />
          <MetadataItemCopy
            label="Twilio SID"
            value={state?.twilioSubAccount?.twilioAccountSid ?? 'Not setup yet'}
          />
          <Typography variant="h4">Transfer phones</Typography>
          <Stack direction="row" sx={{ minWidth: 400, maxWidth: '40%' }}>
            <MetadataItem label="Inbound" value={state?.transferPhoneInbound ?? 'Not setup yet'} />
            <MetadataItem
              label="Outbound"
              value={state?.transferPhoneOutbound ?? 'Not setup yet'}
            />
          </Stack>
          <Typography variant="h4">Volumes</Typography>
          <Stack direction="row" sx={{ minWidth: 400, maxWidth: '50%' }}>
            <VendorSummary vendors={state?.vendorAccounts} />
          </Stack>
        </Card>
      )}
      {!isInDemoMode &&
        (state.id ? (
          <CampaignIvrUrlsCard
            campaignId={state.id}
            companyId={id}
            inboundIvrAudioUrl={state.ivrAudioUrls?.inbound ?? null}
            scheduledIvrAudioUrl={state.ivrAudioUrls?.scheduled ?? null}
            unscheduledIvrAudioUrl={state.ivrAudioUrls?.unscheduled ?? null}
            onSaved={() => refetch()}
          />
        ) : (
          loading && (
            <Card variant="outlined" sx={{ marginTop: 2, padding: 2 }}>
              <Typography variant="h4">IVR Audio URLs</Typography>
              <LinearProgress sx={{ height: '2px', mt: 1 }} />
            </Card>
          )
        ))}
      {!isInDemoMode &&
        (state.id ? (
          <TcrCard campaignId={campaignId} internalCampaignId={state.id} />
        ) : (
          loading && (
            <Card variant="outlined" sx={{ marginTop: 2, padding: 2 }}>
              <Typography variant="h4">TCR Campaign</Typography>
              <LinearProgress sx={{ height: '2px', mt: 1 }} />
            </Card>
          )
        ))}
      <Card variant="outlined" sx={{ marginTop: 2, padding: 2 }}>
        <form onSubmit={handleSubmit}>
          <FormProvider {...telnyxForm}>
            <Typography variant="h4" component="h1">
              Provision
            </Typography>
            <Controller
              control={form.control}
              name="vendor"
              rules={{
                required: true,
              }}
              render={({ field }) => (
                <RadioGroup {...field}>
                  <RadioForm selectedValue={form.watch('vendor')} label="Telnyx">
                    <TelnyxRadio />
                  </RadioForm>
                </RadioGroup>
              )}
            />
            <FormSubmitRow sx={{ marginTop: 2 }} submitText="Provision" loading={provisioning} />
          </FormProvider>
        </form>
      </Card>
      {state.id ? (
        <A2pCards campaignId={campaignId} internalCampaignId={state.id} />
      ) : (
        loading && (
          <Card variant="outlined" sx={{ padding: 2, marginTop: 2 }}>
            <Typography variant="h5">A2P registration</Typography>
            <LinearProgress sx={{ height: '2px', mt: 1 }} />
          </Card>
        )
      )}
    </Container>
  );
};
